import { FormControlRichTextProps, FormElementControlPropsType } from './index';
import { FormComponentBuilder } from '../index';
import Quill from 'quill';
import styles from './FormRichText.styles';
import { Box, ClickAwayListener } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import IconMi from 'components/common/icon/IconMi';
import RichTextLinkTooltip from './FormRichTextParts/RichTextLinkTooltip';
import { useDispatch } from 'react-redux';
import { setFormElementSharedState } from 'store/formSlice';
import RichTextCommandMenu from 'components/common/form/layout/control/FormRichTextParts/RichTextCommandMenu';
import RichTextImageTooltip from './FormRichTextParts/RichTextImageTooltip';

const icons: any = Quill.import('ui/icons');
icons['bold'] = renderToString(<IconMi icon={'bold'} />);
icons['italic'] = renderToString(<IconMi icon={'italic'} />);
icons['underline'] = renderToString(<IconMi icon={'underline'} />);
icons['list']['bullet'] = renderToString(<IconMi icon={'sort-list'} />);
icons['list']['ordered'] = renderToString(<IconMi icon={'sort-numbers'} />);
icons['link'] = renderToString(<IconMi icon={'link'} />);
icons['image'] = renderToString(<IconMi icon={'image'} />);
icons['color'] = renderToString(<IconMi icon={'text-color'} />);

const Inline: any = Quill.import('blots/inline');
const keyboard: any = Quill.import('modules/keyboard');

class CommandBlot extends Inline {
    static blotName = 'command';
    static tagName = 'span';
    static className = 'quill-command-text';

    static formats(): boolean {
        return true;
    }
}
Quill.register(CommandBlot);

export default function FormRichText({
    controlProps,
    elementProps,
}: FormElementControlPropsType<FormControlRichTextProps>) {
    const initialValue = useRef<string>(controlProps.value);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const richTextContainer = useRef<any>(null);
    const quillRef = useRef<any>(null);
    const quillEditorRef = useRef<any>(null);
    const dispatch = useDispatch();

    const [showLinkTooltip, setShowLinkTooltip] = useState<false | number>(false);

    const isCommandMenuActive = useRef<boolean>(false);

    const selectionChangeHandler = (
        range: null | { index: number; length: number },
        oldRange: null | { index: number; length: number },
        source: string,
    ) => {
        if (range) {
            setShowLinkTooltip(false);
            if (range.length == 0) {
                //set cursor without selection (like focus)
                const currentFormat = quillRef.current.getFormat();
                if (currentFormat.link) {
                    setShowLinkTooltip(Date.now());
                }
                //console.log('User cursor is on', range.index);
            } else {
                //selection range
                //console.log('User has highlighted', quill.getText(range.index, range.length));
            }
        } else {
            //like blur, click out off editor
            //console.log('Cursor not in the editor');
        }
    };

    const textChangeHandler = (delta: any, oldDelta: any, source: string) => {
        //hot fix quil 2.0.3 bug with getSemanticHTML .replaceAll(' ', ''&nbsp;'). Revert it
        controlProps.onChange(quillRef.current.getSemanticHTML().replaceAll('&nbsp;', ' '));
        //console.log('textChangeHandler', delta, oldDelta, source);
    };

    useEffect(() => {
        const defaultHeader = keyboard.DEFAULTS.bindings['header enter'].handler;
        keyboard.DEFAULTS.bindings['header enter'].handler = function (range: any, context: any) {
            //prevent default handler for command menu
            if (!isCommandMenuActive.current) {
                defaultHeader.call(this, range, context);
            }
        };

        let toolbarOptions: any = [[{ header: [2, 3, false] }], ['bold', 'italic', 'underline']];
        if (controlProps.settings?.textAlign !== false) {
            toolbarOptions.push([{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }]);
        }
        toolbarOptions = [
            ...toolbarOptions,
            ...[
                [
                    {
                        color: styles.colors,
                    },
                    { list: 'ordered' },
                    { list: 'bullet' },
                ],
                ['link', 'image'],
            ],
        ];

        quillRef.current = new Quill(quillEditorRef.current, {
            readOnly: controlProps.readOnly,
            theme: 'bubble',
            modules: {
                toolbar: toolbarOptions,
                keyboard: {
                    bindings: {
                        'command menu enter': {
                            key: 'Enter',
                            handler: function (range: any, context: any) {
                                return !isCommandMenuActive.current;
                            },
                        },
                        'command menu arrow down': {
                            key: 'ArrowDown',
                            handler: function (range: any, context: any) {
                                return !isCommandMenuActive.current;
                            },
                        },
                        'command menu arrow up': {
                            key: 'ArrowUp',
                            handler: function (range: any, context: any) {
                                return !isCommandMenuActive.current;
                            },
                        },
                    },
                },
            },
        });
        quillRef.current.on('selection-change', selectionChangeHandler);
        quillRef.current.on('text-change', textChangeHandler);

        if (richTextContainer.current) {
            const selectIcons = richTextContainer.current.querySelectorAll('.ql-header .ql-picker-label svg');
            selectIcons.forEach((icon: any) => {
                icon.outerHTML = renderToString(<IconMi icon={'chevron-down'} />);
            });
        }

        return () => {
            quillRef.current.off('selection-change', selectionChangeHandler);
            quillRef.current.off('text-change', textChangeHandler);
        };
    }, []);

    useEffect(() => {
        let pureText = '';
        if (quillRef.current) {
            pureText = quillRef.current.getText().trim();
        }

        dispatch(
            setFormElementSharedState({
                formKey: controlProps.form.formKey,
                componentName: controlProps.name,
                sharedState: {
                    pure_text: pureText,
                },
            }),
        );
    }, [controlProps.value]);

    useEffect(() => {
        quillRef.current.enable(!controlProps.readOnly);
    }, [controlProps.value]);

    useEffect(() => {
        if (!quillRef.current) {
            return;
        }
        if (controlProps.autoFocus) {
            quillRef.current.focus();
        }
    }, [quillRef.current]);

    return (
        <>
            <ClickAwayListener
                onClickAway={(event: Event | React.SyntheticEvent) => {
                    setShowLinkTooltip(false);
                }}
            >
                <Box
                    sx={styles.root}
                    onFocus={() => {
                        setIsFocused(true);
                    }}
                    onBlur={() => {
                        setIsFocused(false);
                    }}
                    className={isFocused || showLinkTooltip ? 'focused' : undefined}
                    ref={richTextContainer}
                >
                    <Box
                        ref={quillEditorRef}
                        dangerouslySetInnerHTML={{
                            __html: initialValue.current,
                        }}
                    />
                    {quillRef.current && (
                        <>
                            <RichTextLinkTooltip
                                showTooltip={showLinkTooltip}
                                setShowTooltip={setShowLinkTooltip}
                                quill={quillRef.current}
                                richTextContainerRef={richTextContainer}
                            />
                            <RichTextCommandMenu
                                quill={quillRef.current}
                                richTextContainerRef={richTextContainer}
                                onChangeStateCommandMenu={(state) => (isCommandMenuActive.current = state)}
                            />
                            {controlProps.settings?.imageAlign !== false && (
                                <RichTextImageTooltip
                                    quill={quillRef.current}
                                    quillIcons={icons}
                                    quillEditorRef={quillEditorRef}
                                    richTextContainerRef={richTextContainer}
                                />
                            )}
                        </>
                    )}
                </Box>
            </ClickAwayListener>
        </>
    );
}

export class FormRichTextBuilder extends FormComponentBuilder {
    prepareProps(): FormControlRichTextProps {
        return {
            ...this.controlProps,
            autoFocus: this.elementProps.componentProps?.autoFocus ?? false,
            settings: this.elementProps.componentProps?.settings ?? {},
        };
    }
}
